import { defineStore } from "pinia";
import fights from "../../Api/fights";
import { Schedule } from "../../Common/Schedule/types";
import { Mat } from "../../Common/types";

export const DeleteFightsMode = {
    All: "all",
    Schedule: "schedule",
    Mat: "mat",
    Category: "category",
} as const;

export type DeleteFightsModeType = (typeof DeleteFightsMode)[keyof typeof DeleteFightsMode];

export type ScheduleMatPair = {
    scheduleId: number;
    matId: number;
};

export type CategoryStatus = {
    parameterId: number;
    name: string;
    totalFights: number;
    hasFinishedFights: boolean;
    isDivided: boolean;
    scheduleMatPairs: ScheduleMatPair[];
};

type ScheduleMatFightsStatus = {
    schedule: Schedule;
    mat: Mat;
};

export type FightsStatusesForDeleteResponse = {
    mats: ScheduleMatFightsStatus[];
    categories: CategoryStatus[];
};

function getIndexKey(scheduleId: number, matId: number): string {
    return `${scheduleId}.${matId}`;
}

function matsForSchedule(mats: Mat[], index: Record<string, ScheduleMatFightsStatus>, scheduleId: number): Mat[] {
    return mats.filter(mat => index[getIndexKey(scheduleId, mat.id)] !== undefined);
}

export const useDeletingFightsStore = defineStore("deletingFights", {
    state: () => ({
        eventId: 0,
        schedules: [] as Schedule[],
        mats: [] as Mat[],
        index: {} as Record<string, ScheduleMatFightsStatus>,
        categories: [] as CategoryStatus[],

        mode: DeleteFightsMode.All as DeleteFightsModeType,
        selectedSchedule: null as Schedule | null,
        selectedMat: null as Mat | null,
        selectedCategoryIds: [] as number[],
        isLoading: false,
    }),

    getters: {
        matsForSelectedSchedule(state): Mat[] {
            if (state.selectedSchedule === null) {
                return [];
            }

            return matsForSchedule(state.mats, state.index, state.selectedSchedule.id);
        },
        scopeReady(state): boolean {
            if (state.mode === DeleteFightsMode.All) {
                return true;
            }
            if (state.mode === DeleteFightsMode.Schedule) {
                return state.selectedSchedule !== null;
            }
            if (state.mode === DeleteFightsMode.Mat) {
                return state.selectedSchedule !== null && state.selectedMat !== null;
            }
            return false;
        },
        categoriesInScope(state): CategoryStatus[] {
            if (state.mode === DeleteFightsMode.All) {
                return state.categories;
            }

            if (!this.scopeReady) {
                return [];
            }

            if (state.mode === DeleteFightsMode.Schedule) {
                return state.categories.filter(category =>
                    category.scheduleMatPairs.some(pair => pair.scheduleId === state.selectedSchedule!.id),
                );
            }

            if (state.mode === DeleteFightsMode.Mat) {
                return state.categories.filter(category =>
                    category.scheduleMatPairs.some(
                        pair => pair.scheduleId === state.selectedSchedule!.id && pair.matId === state.selectedMat!.id,
                    ),
                );
            }

            return [];
        },
        dividedBlocksInScope(state): boolean {
            return state.mode !== DeleteFightsMode.All;
        },
        dividedCategoriesCount(): number {
            return this.categoriesInScope.filter(category => category.isDivided).length;
        },
        finishedCategoriesCount(): number {
            return this.categoriesInScope.filter(category => category.hasFinishedFights).length;
        },
        totalFightsInScope(): number {
            return this.categoriesInScope.reduce((sum, category) => sum + category.totalFights, 0);
        },
        canDeleteScope(): boolean {
            if (!this.scopeReady || this.categoriesInScope.length === 0) {
                return false;
            }

            if (this.dividedBlocksInScope) {
                return this.categoriesInScope.every(category => !category.isDivided && !category.hasFinishedFights);
            }

            return this.categoriesInScope.every(category => !category.hasFinishedFights);
        },
    },

    actions: {
        clearState() {
            this.schedules = [];
            this.mats = [];
            this.index = {};
            this.categories = [];
            this.mode = DeleteFightsMode.All;
            this.selectedSchedule = null;
            this.selectedMat = null;
            this.selectedCategoryIds = [];
        },
        reloadStatuses(): Promise<void> {
            this.isLoading = true;

            return (fights.getFightsStatusesForDelete(this.eventId) as Promise<FightsStatusesForDeleteResponse>)
                .then(response => {
                    const schedulesMap: Record<number, Schedule> = {};
                    const matsMap: Record<number, Mat> = {};
                    const index: Record<string, ScheduleMatFightsStatus> = {};

                    response.mats.forEach(el => {
                        schedulesMap[el.schedule.id] = el.schedule;
                        matsMap[el.mat.id] = el.mat;
                        index[getIndexKey(el.schedule.id, el.mat.id)] = el;
                    });

                    this.schedules = Object.values(schedulesMap);
                    this.mats = Object.values(matsMap);
                    this.index = index;
                    this.categories = response.categories;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        setMode(mode: DeleteFightsModeType) {
            this.mode = mode;
            this.selectedSchedule = null;
            this.selectedMat = null;

            if (mode === DeleteFightsMode.Schedule || mode === DeleteFightsMode.Mat) {
                this.selectedSchedule = this.schedules[0] ?? null;
            }

            if (mode === DeleteFightsMode.Mat && this.selectedSchedule !== null) {
                this.selectedMat = matsForSchedule(this.mats, this.index, this.selectedSchedule.id)[0] ?? null;
            }
        },
        selectSchedule(schedule: Schedule) {
            this.selectedSchedule = schedule;
            this.selectedMat = matsForSchedule(this.mats, this.index, schedule.id)[0] ?? null;
        },
        selectMat(mat: Mat) {
            this.selectedMat = mat;
        },
        setSelectedCategories(categories: CategoryStatus[]) {
            this.selectedCategoryIds = categories.map(category => category.parameterId);
        },
        setEventId(eventId: number) {
            this.eventId = eventId;
        },
        deleteFights(): Promise<void> {
            const { eventId, mode, selectedSchedule, selectedMat } = this;

            if (mode === DeleteFightsMode.All) {
                return fights.deleteFightsForAllSchedules(eventId);
            }

            if (mode === DeleteFightsMode.Schedule) {
                return fights.deleteFightsByWholeSchedule(eventId, selectedSchedule!.id);
            }

            if (mode === DeleteFightsMode.Mat) {
                return fights.deleteFightsByMat(eventId, selectedSchedule!.id, selectedMat!.id);
            }

            return Promise.resolve();
        },
        deleteSelectedCategories(): Promise<void> {
            const { eventId, selectedCategoryIds } = this;

            return selectedCategoryIds
                .reduce((promise, parameterId) => {
                    return promise.then(() => fights.deleteFightsByCategory(eventId, parameterId));
                }, Promise.resolve())
                .then(() => this.reloadStatuses())
                .then(() => {
                    this.selectedCategoryIds = [];
                });
        },
    },
});
